import { Play, Lock } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { useJsonLd } from "@/hooks/useJsonLd";
import { SITE } from "@/constants/testIds";

const YOUTUBE_CHANNEL_URL =
  "https://www.youtube.com/channel/UChVXTlcq0PpmENNSjeia-Fw";

// Demo video card. Click the poster to open the YeboNadi YouTube channel
// in a new tab.
export default function DemoVideoCard({ id = "demo" }) {
  useJsonLd("demo-video", {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: "See how YeboNadi seals a recording (15-second demo)",
    description:
      "A 15-second walkthrough of how YeboNadi seals a voice recording, AES-256 encryption, SHA-256 fingerprint, timestamp, and GPS embedded at the moment of capture.",
    thumbnailUrl:
      "/logo.png",
    uploadDate: "2026-02-11",
    contentUrl: YOUTUBE_CHANNEL_URL,
    embedUrl: YOUTUBE_CHANNEL_URL,
    publisher: {
      "@type": "Organization",
      name: "YeboNadi",
      url: "https://yebonadi.com",
    },
  });

  const handlePlay = () => {
    trackEvent("demo_video_play", { placement: "home", target: "youtube_channel" });
  };

  return (
    <section
      id={id}
      className="py-16 md:py-24 bg-white scroll-mt-24"
      data-testid={SITE.demoSection}
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-[color:var(--yn-primary)]">
            15 seconds
          </p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[color:var(--yn-ink)]">
            See how it works.
          </h2>
        </div>

        <div className="relative rounded-2xl overflow-hidden border border-[color:var(--yn-border)] bg-[color:var(--yn-ink)] aspect-video shadow-sm">
          {/* Poster frame, stylised "sealed record" screen */}
          <div className="absolute inset-0 flex items-center justify-center px-6">
            <div className="w-full max-w-md rounded-xl border border-white/10 bg-white/[0.04] p-5 sm:p-6 backdrop-blur-sm">
              <div className="flex items-center gap-2 text-white/80 text-xs font-medium">
                <Lock size={14} />
                Sealed record
              </div>
              <div className="mt-4 h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full w-2/3 rounded-full"
                  style={{ background: "var(--yn-primary)" }}
                />
              </div>
              <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 text-[11px] sm:text-xs">
                <div>
                  <dt className="text-white/50 uppercase tracking-wider">
                    Captured
                  </dt>
                  <dd className="text-white/90 mt-1 font-medium">
                    Nov 14 · 11:47 PM
                  </dd>
                </div>
                <div>
                  <dt className="text-white/50 uppercase tracking-wider">
                    Location
                  </dt>
                  <dd className="text-white/90 mt-1 font-medium">
                    33.3062° N, 111.8413° W
                  </dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-white/50 uppercase tracking-wider">
                    Fingerprint
                  </dt>
                  <dd className="text-white/85 mt-1 font-mono text-[10px] sm:text-[11px] break-all">
                    9f2b7a3c8d4e6f10a7b3c8d4e6f10a7b3c8d4e6f10a7b3c8d4e6f10a7b3c8
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          <a
            href={YOUTUBE_CHANNEL_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handlePlay}
            data-testid={SITE.demoPlayBtn}
            aria-label="Watch YeboNadi demos on YouTube (opens in a new tab)"
            className="absolute inset-0 flex items-center justify-center group focus:outline-none"
          >
            <span
              className="inline-flex items-center justify-center h-16 w-16 sm:h-20 sm:w-20 rounded-full text-white shadow-xl transition-transform group-hover:scale-105 group-focus-visible:ring-4 group-focus-visible:ring-white/40"
              style={{ background: "var(--yn-primary)" }}
            >
              <Play size={28} fill="currentColor" strokeWidth={0} />
            </span>
          </a>
        </div>

        <p className="mt-4 text-center text-sm text-[color:var(--yn-muted)]">
          Opens the YeboNadi channel on YouTube in a new tab.
        </p>

        <p className="mt-6 text-center text-[color:var(--yn-muted)]">
          Record. Sealed in the moment. Released only when the process says so.
        </p>
      </div>
    </section>
  );
}
